'use client'

/**
 * Portal target for the mobile header title.
 *
 * Pages render <MobileHeaderTitle>...</MobileHeaderTitle> from anywhere
 * inside their tree. The mobile shell's <header> hosts the slot element;
 * children rendered via this provider are teleported into that slot.
 *
 * Desktop is unaffected — the slot only mounts in MobileShell, which itself
 * is only rendered <lg.
 *
 * Mounting an empty slot or no MobileHeaderTitle at all is fine; the shell
 * falls back to a default title (route map / logo on /).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

type MobileHeaderContextValue = {
  /** The DOM node to portal title content into. Null until shell mounts. */
  titleSlot: HTMLElement | null
  /** The DOM node to portal trailing-action content into. */
  actionsSlot: HTMLElement | null
  setTitleSlot: (el: HTMLElement | null) => void
  setActionsSlot: (el: HTMLElement | null) => void
}

const MobileHeaderContext = createContext<MobileHeaderContextValue | null>(null)

export function MobileHeaderProvider({ children }: { children: ReactNode }) {
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null)
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null)

  return (
    <MobileHeaderContext.Provider
      value={{ titleSlot, actionsSlot, setTitleSlot, setActionsSlot }}
    >
      {children}
    </MobileHeaderContext.Provider>
  )
}

function useMobileHeaderSlots() {
  return useContext(MobileHeaderContext)
}

/**
 * Page-level title for the mobile header. Render anywhere in your page tree:
 *
 *   <MobileHeaderTitle>Brand name</MobileHeaderTitle>
 *
 * On desktop this renders nothing. On mobile it portals into the shell header.
 * If the shell isn't mounted yet (first paint), this defers rendering until it is.
 */
export function MobileHeaderTitle({ children }: { children: ReactNode }) {
  const ctx = useMobileHeaderSlots()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  if (!mounted) return null
  if (!ctx?.titleSlot) return null
  return createPortal(<>{children}</>, ctx.titleSlot)
}

/**
 * Page-level trailing actions for the mobile header (e.g. "New Campaign"
 * button on /campaigns). Replaces the default search icon for that page.
 *
 * Note: providing this REPLACES the search button on that page. To keep
 * search alongside, render <MobileHeaderSearchButton /> inside your actions.
 */
export function MobileHeaderActions({ children }: { children: ReactNode }) {
  const ctx = useMobileHeaderSlots()
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  if (!mounted) return null
  if (!ctx?.actionsSlot) return null
  return createPortal(<>{children}</>, ctx.actionsSlot)
}

/**
 * Shell-internal callback ref factories. Pass these to the slot div's `ref={...}`
 * so the slot DOM node is registered with the context as soon as it mounts and
 * cleared on unmount. Don't use `useRef`+`ref.current` — refs don't trigger renders.
 *
 * Usage: const titleRef = useMobileHeaderTitleSlotRef(); <div ref={titleRef} />
 */
export function useMobileHeaderTitleSlotRef() {
  const ctx = useContext(MobileHeaderContext)
  const setTitleSlot = ctx?.setTitleSlot
  return useCallback(
    (el: HTMLElement | null) => {
      setTitleSlot?.(el)
    },
    [setTitleSlot],
  )
}

export function useMobileHeaderActionsSlotRef() {
  const ctx = useContext(MobileHeaderContext)
  const setActionsSlot = ctx?.setActionsSlot
  return useCallback(
    (el: HTMLElement | null) => {
      setActionsSlot?.(el)
    },
    [setActionsSlot],
  )
}

/**
 * Whether a page-supplied title is currently mounted. The shell uses this to
 * suppress the default fallback title when a page provides its own.
 */
export function useHasMobileHeaderTitle(): boolean {
  const ctx = useContext(MobileHeaderContext)
  const slot = ctx?.titleSlot ?? null
  const [hasChildren, setHasChildren] = useState(false)

  useEffect(() => {
    if (!slot) {
      setHasChildren(false)
      return undefined
    }
    const update = () => setHasChildren(slot.childNodes.length > 0)
    update()
    const obs = new MutationObserver(update)
    obs.observe(slot, { childList: true })
    return () => obs.disconnect()
  }, [slot])

  return hasChildren
}

/** Same pattern for the trailing-actions slot. */
export function useHasMobileHeaderActions(): boolean {
  const ctx = useContext(MobileHeaderContext)
  const slot = ctx?.actionsSlot ?? null
  const [hasChildren, setHasChildren] = useState(false)

  useEffect(() => {
    if (!slot) {
      setHasChildren(false)
      return undefined
    }
    const update = () => setHasChildren(slot.childNodes.length > 0)
    update()
    const obs = new MutationObserver(update)
    obs.observe(slot, { childList: true })
    return () => obs.disconnect()
  }, [slot])

  return hasChildren
}
